// Frontend platform-event forwarding is proxy-only. The browser must never hold
// Events API credentials and must never post events through the coaching product
// API. Emission is allowed solely through a configured same-origin proxy URL
// (VITE_PLATFORM_EVENTS_PROXY_URL); with no proxy configured this module is a
// strict no-op. Metadata is redacted before leaving the app and failures are
// swallowed so observability never breaks product flows. Mirrors the shape
// shipped in consulting-dashboard/src/lib/platform-events.ts.

const platformEventsProxyUrl = (import.meta.env.VITE_PLATFORM_EVENTS_PROXY_URL ?? "").trim();

const SENSITIVE_KEY_RE = /(token|cookie|otp|secret|key|password|authorization|jwt|session)/i;

function redactMetadata(value: unknown, depth = 0): unknown {
  if (depth > 4) return "[truncated]";
  if (Array.isArray(value)) return value.map((item) => redactMetadata(item, depth + 1));
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      out[key] = SENSITIVE_KEY_RE.test(key) ? "[redacted]" : redactMetadata(val, depth + 1);
    }
    return out;
  }
  return value;
}

export interface PlatformEventEmitResult {
  ok: boolean;
  status: "sent" | "skipped";
}

/**
 * Resolve the configured proxy URL, asserting it is same-origin (or relative).
 * An absolute cross-origin URL is rejected with `null` so a misconfigured proxy
 * silently disables emission instead of leaking events to an arbitrary origin.
 */
export function resolveSameOriginProxyUrl(
  rawUrl: string,
  origin: string = typeof window !== "undefined" ? window.location.origin : ""
): string | null {
  const url = rawUrl.trim();
  if (!url) return null;
  if (url.startsWith("/")) return url; // relative, always same-origin
  if (!origin) return null;
  try {
    return new URL(url, origin).origin === origin ? url : null;
  } catch {
    return null;
  }
}

/**
 * Post a sanitized platform-event envelope to the configured same-origin proxy.
 * No-op (returns `{ ok: true, status: "skipped" }`) when no proxy is configured
 * or when the configured proxy is not same-origin. Best-effort: network failures
 * are swallowed and never surfaced to the product UI.
 */
export async function emitPlatformEvents(
  events: Array<Record<string, unknown>>,
  proxyUrl: string = platformEventsProxyUrl
): Promise<PlatformEventEmitResult> {
  const target = resolveSameOriginProxyUrl(proxyUrl);
  if (!target) return { ok: true, status: "skipped" };
  const payload = { events: events.map((event) => redactMetadata(event) as Record<string, unknown>) };
  try {
    await fetch(target, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      credentials: "include",
      keepalive: true
    });
    return { ok: true, status: "sent" };
  } catch {
    // Event emission is best-effort observability; never surface to the user.
    return { ok: false, status: "sent" };
  }
}

/** True when a usable same-origin platform-events proxy is configured. */
export function isPlatformEventsProxyConfigured(): boolean {
  return resolveSameOriginProxyUrl(platformEventsProxyUrl) !== null;
}
